import { Config } from 'config/envConfig';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Switch } from 'antd';
import { Button, FolderListViewer, SubmitButton } from '@quyva/shared-ui';
// Repo-name rules are validated once by the parent, reaching here via the `error` prop.
import { useRepoSubmit } from '../hooks/useRepoSubmit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogleDrive } from '@fortawesome/free-brands-svg-icons';
import {
    faCircleXmark,
    faFolderPlus,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import GoogleDrivePicker from 'google-drive-picker';
import { PublicClientApplication } from '@azure/msal-browser';

import { ProcessType, TaskProgressType, Platforms,RepoType, ModelTiers, vectorStores } from '@quyva/common';

import { getUrl } from './pdfDisplay/utils';
import { RepoConfig } from './RepoConfig';
import { OneDrivePickerV8Modal } from './oneDriveAPI/OneDrivePickerV8Modal';
const AddCloudRepo = ({
    reponame,
    setAddNewMode,
    getAllReposData,
    socket,
    isAdmin,
    visionRepo,
    knowledgeGraph,
    onChangeVision,
    onChangeKnowledgeGraph,
    visionModel,
    setVisionModel,
    embeddingModel,
    setEmbeddingModel,
    error,
    user,
    modelTierRepo,
    setModelTierRepo,
    isLoadingModels,
    availableCustomModels,
    ModelTiers,
    imageResolution,
    setImageResolution,
    imageResolutionDropdown,
    availableEmbeddingModels,
    modelsLoading,
}) => {
    const { isSubmitting, runSubmit, WarningModal } = useRepoSubmit(
        'cloud repository creation'
    );
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [platform, setPlatform] = useState(null); // New state for platform
    const [authTocken, setauthTocken] = useState('');
    const [authToken, setauthToken] = useState('');
    const [openPicker, authRes] = GoogleDrivePicker();
    const [oneDriveRefreshToken, setOneDriveRefreshToken] = useState(null);
    // OneDrive File Picker v8 state: opened after the MSAL sign-in below.
    const [oneDrivePickerOpen, setOneDrivePickerOpen] = useState(false);
    const [oneDriveAccount, setOneDriveAccount] = useState(null);
    const [oneDriveGraphToken, setOneDriveGraphToken] = useState(null);
    const [googleDriveConfig, setGoogleDriveConfig] = useState({
        clientId: '',
        pickerApi:'',
    });
    const [oneDriveConfig, setOneDriveConfig] = useState({
        clientId: '',
        tenantId:'',
    });
    // Dynamically load the OneDrive SDK script
    // Dynamically load the OneDrive SDK when the component mounts
    const msalInstance = useMemo(() => {
        const msalConfig = {
            auth: {
                clientId: oneDriveConfig.clientId,
                authority: 'https://login.microsoftonline.com/common',
                redirectUri: window.location.origin,
            },
            cache: {
                cacheLocation: 'localStorage',
            },
        };
        return new PublicClientApplication(msalConfig);
    }, [oneDriveConfig.clientId]);
    // Sign in once with MSAL (Graph token for the backend), then open the
    // OneDrive File Picker v8. The picker (rendered by OneDrivePickerV8Modal)
    // acquires its own SharePoint-resource token silently from this session.
    const loginToOneDrive = async () => {
        try {
            await msalInstance.initialize();
            const loginResponse = await msalInstance.loginPopup({
                scopes: ['Files.ReadWrite.All', 'offline_access'],
            });

            msalInstance.setActiveAccount(loginResponse.account);
            // Graph token consumed by the backend (onedrive_storage.py) as token_file.
            setauthToken(loginResponse.accessToken);
            setOneDriveAccount(loginResponse.account);
            setOneDriveGraphToken(loginResponse.accessToken);
            setOneDrivePickerOpen(true);
        } catch (error) {
            console.error('OneDrive login error:', error);
        }
    };

    useEffect(() => {
        const fetchGoogleDriveConfig = async () => {
            try {
                const dynamicUrl = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/getCloudConfig?keys=googleClientDriveId,googlePickerApi`
                );
                const response = await axios.get(dynamicUrl);
                if (response.data) {
                    setGoogleDriveConfig({
                        clientId: response.data.googleClientDriveId || '',
                        pickerApi: response.data.googlePickerApi || '',
                    });
                }
            } catch (error) {
                console.error('Error fetching Google Drive config:', error.message);
            }
        };
        fetchGoogleDriveConfig();
    }, []);

    useEffect(() => {
        const fetchOneDriveConfig = async () => {
            try {
                const dynamicUrl = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/getCloudConfig?keys=oneDriveClientId,oneDriveTenantId`
                );
                const response = await axios.get(dynamicUrl);
                if (response.data) {
                    setOneDriveConfig({
                        clientId: response.data.oneDriveClientId || '',
                        tenantId: response.data.oneDriveTenantId || '',
                    });
                }
            } catch (error) {
                console.error('Error fetching OneDrive config:', error.message);
            }
        };
        fetchOneDriveConfig();
    }, []);

    const handleOpenPicker = async (platformName) => {
        try {
            setPlatform(platformName); // Set the platform (Google Drive or OneDrive)
            if (platformName === Platforms.GOOGLE_DRIVE) {
                openPicker({
                    clientId: googleDriveConfig.clientId,
                    developerKey: googleDriveConfig.pickerApi,
                    viewId: 'ALL', // Set view to folders
                    token: authTocken.acess_token,
                    showUploadView: false, // Disable file uploads
                    showUploadFolders: false,
                    supportDrives: true,
                    multiselect: false, // Allow only one folder selection
                    setSelectFolderEnabled: true,
                    setIncludeFolders: true,
                    callbackFunction: (data) => {
                        if (data.action === 'cancel') {
                            console.log('User clicked cancel/close button');
                        } else if (data.docs && data.docs.length > 0) {
                            const selectedItem = data.docs[0];
                            if (
                                selectedItem.mimeType ===
                                'application/vnd.google-apps.folder'
                            ) {
                                setSelectedFolder(selectedItem); // Update selected folder
                            } else {
                                // It's a file, so show an error and ask the user to select a folder
                                console.error(
                                    'Only folder selection is allowed.'
                                );
                                alert('Please select a folder, not a file.');
                            }
                        }
                    },
                });
            } else if (platformName === Platforms.ONE_DRIVE) {
                loginToOneDrive();
            }
        } catch (error) {
            console.error(`Error opening ${platformName} Picker:`, error);
        }
    };
    useEffect(() => {
        if (authRes) {
            setauthTocken(authRes);
        }
    }, [authRes]);

    const submitForm = async (e) => {
        e.preventDefault();

        if (!selectedFolder || error) {
            return;
        }

        const folderData = {
            id: user.email,
            // name:
            //     platform === 'OneDrive'
            //         ? selectedFolder.value[0].name
            //         : selectedFolder.name,
            name: reponame,
            visionRepo: visionRepo,
            knowledgeGraph: knowledgeGraph,
            visionModel: visionModel,
            vector_store_type: vectorStores.QDRANT,
            summary_flag: false,
            embedding_model: embeddingModel,
            image_res: imageResolution,
            storage_type: 'cloud',
            repotype: RepoType.CONTENT,
        };

        const requestBody = {
            platform: platform,
            email: user.email,
            //repo_name: reponame,
            repo_name: reponame,
            // platform === 'OneDrive'
            //     ? selectedFolder.value[0].name
            //     : selectedFolder.name,
            token_file:
                platform === Platforms.ONE_DRIVE
                    ? authToken
                    : authTocken.access_token,
            folder_id:
                platform === Platforms.ONE_DRIVE
                    ? selectedFolder.value[0].id
                    : selectedFolder.id, // Get folder ID from selected folder
        };

        runSubmit(async () => {
        setAddNewMode(false); //close the form

        try {
            const status = {
                message: `Started repository creation with name ${reponame}`,
                percentageCompletion: 0,
                process: ProcessType.FILE_UPLOAD,
                progress: TaskProgressType.STARTED,
                repoId: null,
                repoName: reponame,
                statusCode: 200,
                timeStamp: new Date(),
                userMail: user.email,
            };

            socket.emit('statusUpdate', status);
            const repoDataString = encodeURIComponent(
                JSON.stringify(folderData)
            );

            // Use the shared axios instance (NOT fetch) for both calls. Its
            // interceptors send the auth cookie to the Node server and, on a
            // 401 from a rotated token, refresh and retry the request. Raw fetch
            // skipped that, so a stale Authorization header 401'd silently and
            // the repo was never created — leaving the 0% notification orphaned.
            try {
                // 1) Node: register the cloud folder + create the empty repo.
                await axios.post(
                    getUrl(
                        `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/new_empty_repo/${repoDataString}`
                    )
                );

                // 2) FastAPI: kick off ingestion of the drive folder.
                await axios.post(
                    getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/process_drive_info`
                    ),
                    requestBody
                );

                console.log('Repository created successfully');
                getAllReposData(); // Refresh the repository data
            } catch (apiError) {
                console.error('Error creating cloud repository:', apiError);
                // Resolve the optimistic 0% notification into a visible failure
                // in the Tasks/notifications panel (same place progress shows).
                // No blocking alert — the failed status update is the user-facing
                // signal, consistent with the rest of the app.
                socket.emit('statusUpdate', {
                    ...status,
                    message: `Repository creation failed for ${reponame}`,
                    progress: TaskProgressType.FAILED,
                    statusCode: apiError?.response?.status || 500,
                    timeStamp: new Date(),
                });
            }
        } catch (err) {
            console.error('Error during API requests:', err);
        }
        }); // end runSubmit callback
    };

    return (
        <form onSubmit={submitForm}>
            <div className='flex flex-col justify-center items-center min-h-[25vh] bg-backgrounds-white rounded-xl my-4 border-2 border-dashed border-backgrounds-primary300'>
                <div className='text-center text-texts-secondary300 mb-4'>
                    Connect with either OneDrive or Google Drive to sync your
                    files with the repository.
                </div>
                <div className='flex space-x-6 items-center'>
                    <div
                        className='flex flex-col items-center cursor-pointer hover:border hover:border-backgrounds-primary300 p-4 rounded-lg transition-all duration-200'
                        onClick={() => handleOpenPicker(Platforms.GOOGLE_DRIVE)}
                    >
                        <FontAwesomeIcon
                            icon={faGoogleDrive}
                            size='3x'
                            className='text-backgrounds-primary300'
                        />
                        <span className='mt-2 font-semibold text-texts-secondary300'>
                            Google Drive
                        </span>
                    </div>
                    <div className='border-l-2 h-16 border-gray-300'></div>

                    <div
                        className='flex flex-col items-center cursor-pointer hover:border hover:border-backgrounds-primary300 p-4 rounded-lg transition-all duration-200'
                        onClick={() => handleOpenPicker(Platforms.ONE_DRIVE)}
                    >
                        <img
                            src='/OneDriveIcon.png'
                            alt='OneDrive'
                            className='w-12 h-12'
                        />
                        <span className='mt-2 font-semibold text-texts-secondary300'>
                            OneDrive
                        </span>
                    </div>
                </div>
            </div>
            <div className='pt-4'>
                <FolderListViewer
                    className='mt-5'
                    selectedFolder={selectedFolder}
                    platform={platform}
                />
                {/* {false && (
                    <div className='flex justify-between items-center mt-3'>
                        <div className='flex items-center justify-center mt-4'>
                            <div className='mr-2 font-semibold text-texts-secondary300'>
                                Vision Processing
                            </div>
                            <Switch
                                checked={visionRepo}
                                onChange={onChangeVision}
                            />
                        </div>
                    </div>
                )} */}
                <div className='items-center justify-between mt-5 w-full'>
                    <RepoConfig
                        modelTierRepo={modelTierRepo}
                        setModelTierRepo={setModelTierRepo}
                        visionModel={visionModel}
                        setVisionModel={setVisionModel}
                        embeddingModel={embeddingModel}
                        setEmbeddingModel={setEmbeddingModel}
                        isLoadingModels={isLoadingModels}
                        availableCustomModels={availableCustomModels}
                        ModelTiers={ModelTiers}
                        imageResolution={imageResolution}
                        setImageResolution={setImageResolution}
                        imageResolutionDropdown={imageResolutionDropdown}
                        knowledgeGraph={knowledgeGraph}
                        onChangeKnowledgeGraph={onChangeKnowledgeGraph}
                        isAdmin={isAdmin}
                        availableEmbeddingModels={availableEmbeddingModels}
                        modelsLoading={modelsLoading}
                    />
                </div>
            </div>
            {error && (
                <div className='flex justify-center items-center text-buttons-alert400 mt-3'>
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className='mr-2'
                    />
                    {error}
                </div>
            )}
            <div className='flex items-center justify-center mt-4'>
                <div className='flex items-center justify-between w-full'>
                    <Button
                        type='danger'
                        icon={faCircleXmark}
                        text={'Cancel'}
                        tile={'Cancel and close the modal'}
                        className='w-1/3'
                        onClick={() => setAddNewMode(false)}
                    />
                    <SubmitButton
                        icon={faFolderPlus}
                        text='Add'
                        submittingText='Creating...'
                        isSubmitting={isSubmitting}
                        disabled={error || !selectedFolder || !reponame}
                        title='Upload files and add new repository'
                    />
                </div>
            </div>
        {oneDrivePickerOpen && oneDriveAccount && oneDriveGraphToken && (
                <OneDrivePickerV8Modal
                    msalInstance={msalInstance}
                    account={oneDriveAccount}
                    graphToken={oneDriveGraphToken}
                    onPicked={(folder) => {
                        setPlatform(Platforms.ONE_DRIVE);
                        setSelectedFolder({ value: [folder] });
                        setOneDrivePickerOpen(false);
                    }}
                    onClose={() => setOneDrivePickerOpen(false)}
                />
            )}
        {WarningModal}
        </form>
    );
};

export default AddCloudRepo;
